import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import ConfirmDialog, { type ConfirmOptions } from '../components/ui/ConfirmDialog';

interface ConfirmContextType {
    ask: (options: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextType | undefined>(undefined);

export function ConfirmProvider({ children }: { children: ReactNode }) {
    const [isOpen, setIsOpen] = useState(false);
    const [options, setOptions] = useState<ConfirmOptions>({ title: '', message: '' });
    const [resolveRef, setResolveRef] = useState<((value: boolean) => void) | null>(null);

    const ask = useCallback((options: ConfirmOptions) => {
        setOptions(options);
        setIsOpen(true);
        return new Promise<boolean>((resolve) => {
            setResolveRef(() => resolve);
        });
    }, []);

    const handleConfirm = () => {
        if (resolveRef) resolveRef(true);
        setIsOpen(false);
    };

    const handleCancel = () => {
        if (resolveRef) resolveRef(false);
        setIsOpen(false);
    };

    return (
        <ConfirmContext.Provider value={{ ask }}>
            {children}
            <ConfirmDialog
                isOpen={isOpen}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
                {...options}
            />
        </ConfirmContext.Provider>
    );
}

export function useConfirm() {
    const context = useContext(ConfirmContext);
    if (!context) {
        throw new Error('useConfirm must be used within a ConfirmProvider');
    }
    return context;
}
